import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";

// Competitor pricing verified February 2026
// Most comparable tiers for podcast/content creators

// Otter.ai pricing (Pro plan - most popular for creators)
// Free: 300 min/month, Pro: $16.99/mo for 1200 min (20 hrs), Business: $30/mo for 6000 min (100 hrs)
const OTTER_TIERS = [
  { name: "Free", price: 0, minutes: 300 },
  { name: "Pro", price: 16.99, minutes: 1200 },
  { name: "Business", price: 30, minutes: 6000 },
];

// Descript pricing (Creator plan - comparable for podcast work)
// Free: 60 min, Hobbyist: $12/mo for 600 min, Creator: $24/mo for 1800 min, Business: $44/mo for 2400 min
const DESCRIPT_TIERS = [
  { name: "Free", price: 0, minutes: 60 },
  { name: "Hobbyist", price: 12, minutes: 600 },
  { name: "Creator", price: 24, minutes: 1800 },
  { name: "Business", price: 44, minutes: 2400 },
];

// Rev pricing (Essentials - designed for content creators)
// Free: 45 min, Essentials: $29.99/mo for 5000 min, Pro: $59.99/mo for 10000 min
const REV_TIERS = [
  { name: "Free", price: 0, minutes: 45 },
  { name: "Essentials", price: 29.99, minutes: 5000 },
  { name: "Pro", price: 59.99, minutes: 10000 },
];

// EchoNour pricing
const ECHONOUR_FREE_MINUTES = 45;
const ECHONOUR_PER_MINUTE = 0.10; // $0.10/min = $6/hour

const CONTENT_TYPES = [
  { id: "podcast", label: "Podcast", icon: "🎙️" },
  { id: "interview", label: "Interview", icon: "🎤" },
  { id: "lecture", label: "Lecture", icon: "📚" },
  { id: "meeting", label: "Meeting", icon: "💼" },
];

function findCheapestTier(tiers: typeof OTTER_TIERS, minutesNeeded: number) {
  // Find the cheapest tier that covers the usage
  for (const tier of tiers) {
    if (tier.minutes >= minutesNeeded) {
      return { ...tier, sufficient: true };
    }
  }
  // If no tier covers it, return the highest tier with a note
  const highest = tiers[tiers.length - 1];
  return { ...highest, sufficient: false };
}

function calculateCosts(hoursPerMonth: number) {
  const minutesNeeded = hoursPerMonth * 60;

  // EchoNour: Pay-as-you-go after free tier
  const echonourBillableMinutes = Math.max(0, minutesNeeded - ECHONOUR_FREE_MINUTES);
  const echonourCost = echonourBillableMinutes * ECHONOUR_PER_MINUTE;

  // Competitors: Find cheapest tier that covers usage
  const otter = findCheapestTier(OTTER_TIERS, minutesNeeded);
  const descript = findCheapestTier(DESCRIPT_TIERS, minutesNeeded);
  const rev = findCheapestTier(REV_TIERS, minutesNeeded);

  return {
    echonour: {
      cost: echonourCost,
      billableMinutes: echonourBillableMinutes,
      freeMinutes: Math.min(minutesNeeded, ECHONOUR_FREE_MINUTES),
    },
    otter: {
      cost: otter.price,
      tier: otter.name,
      includedMinutes: otter.minutes,
      sufficient: otter.sufficient,
    },
    descript: {
      cost: descript.price,
      tier: descript.name,
      includedMinutes: descript.minutes,
      sufficient: descript.sufficient,
    },
    rev: {
      cost: rev.price,
      tier: rev.name,
      includedMinutes: rev.minutes,
      sufficient: rev.sufficient,
    },
  };
}

export default function CostCalculatorPage() {
  const [hours, setHours] = useState(10);
  const [contentType, setContentType] = useState("podcast");

  const costs = useMemo(() => calculateCosts(hours), [hours]);

  // Calculate savings vs each competitor
  const savingsVsOtter = costs.otter.cost - costs.echonour.cost;
  const savingsVsDescript = costs.descript.cost - costs.echonour.cost;
  const savingsVsRev = costs.rev.cost - costs.echonour.cost;
  const maxSavings = Math.max(savingsVsOtter, savingsVsDescript, savingsVsRev, 0);

  // For small usage where EchoNour might be more expensive due to PAYG vs free tiers
  const isCheapest =
    costs.echonour.cost <= costs.otter.cost &&
    costs.echonour.cost <= costs.descript.cost &&
    costs.echonour.cost <= costs.rev.cost;

  // Set document title and meta tags on mount
  useEffect(() => {
    document.title = "Podcast Transcription Cost Calculator — Compare Prices | EchoNour";

    // Set meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "Compare transcription costs across EchoNour, Otter, Descript, and Rev. Enter your monthly audio hours and see exactly what you'll pay."
    );

    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Podcast Transcription Cost Calculator",
      url: "https://echonour.com/tools/cost-calculator",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description:
        "Compare transcription costs across EchoNour, Otter.ai, Descript, and Rev. Enter your monthly audio hours and see exactly what you'll pay.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      creator: {
        "@type": "Organization",
        name: "EchoNour",
        url: "https://echonour.com",
      },
    };

    let script = document.getElementById("cost-calculator-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "cost-calculator-jsonld";
      script.setAttribute("type", "application/ld+json");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      // Cleanup on unmount
      const el = document.getElementById("cost-calculator-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Transcription Cost Calculator
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
            Compare transcription costs across services. Enter your monthly audio hours and see
            exactly what you'll pay.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:p-8">
          {/* Content Type Selector */}
          <div className="mb-8">
            <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
              What are you transcribing? (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setContentType(type.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                    contentType === type.id
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {type.icon} {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Hours Slider */}
          <div className="mb-8">
            <div className="mb-3 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Hours of audio per month
              </label>
              <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {hours} {hours === 1 ? "hour" : "hours"}
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="100"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value, 10))}
              className="h-3 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-indigo-600 dark:bg-gray-700"
            />
            <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>1 hour</span>
              <span>25 hrs</span>
              <span>50 hrs</span>
              <span>75 hrs</span>
              <span>100 hrs</span>
            </div>
          </div>

          {/* Savings Badge */}
          {isCheapest && maxSavings > 0 && (
            <div className="mb-6 flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-green-800 dark:bg-green-900 dark:text-green-200">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-semibold">
                  Save up to ${maxSavings.toFixed(2)}/month with EchoNour
                </span>
              </div>
            </div>
          )}

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Service
                  </th>
                  <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Plan
                  </th>
                  <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                    Monthly Cost
                  </th>
                  <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                    vs EchoNour
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {/* EchoNour Row - Highlighted */}
                <tr className="bg-indigo-50 dark:bg-indigo-900/30">
                  <td className="py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                        EchoNour
                      </span>
                      {isCheapest && (
                        <span className="rounded bg-green-500 px-2 py-0.5 text-xs font-medium text-white">
                          Best Value
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-300">
                    Pay-as-you-go
                    <div className="text-xs text-gray-500">
                      {costs.echonour.freeMinutes > 0 && (
                        <span>{costs.echonour.freeMinutes} min free + </span>
                      )}
                      ${ECHONOUR_PER_MINUTE.toFixed(2)}/min
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                      ${costs.echonour.cost.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 text-right text-sm text-gray-500">—</td>
                </tr>

                {/* Otter.ai Row */}
                <tr>
                  <td className="py-4">
                    <span className="font-medium text-gray-900 dark:text-white">Otter.ai</span>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-300">
                    {costs.otter.tier}
                    <div className="text-xs text-gray-500">
                      {Math.floor(costs.otter.includedMinutes / 60)} hrs included
                      {!costs.otter.sufficient && (
                        <span className="text-amber-600"> (max tier)</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      ${costs.otter.cost.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 text-right text-sm">
                    {savingsVsOtter > 0 ? (
                      <span className="text-green-600 dark:text-green-400">
                        +${savingsVsOtter.toFixed(2)} more
                      </span>
                    ) : savingsVsOtter < 0 ? (
                      <span className="text-gray-500">
                        ${Math.abs(savingsVsOtter).toFixed(2)} less
                      </span>
                    ) : (
                      <span className="text-gray-500">Same</span>
                    )}
                  </td>
                </tr>

                {/* Descript Row */}
                <tr>
                  <td className="py-4">
                    <span className="font-medium text-gray-900 dark:text-white">Descript</span>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-300">
                    {costs.descript.tier}
                    <div className="text-xs text-gray-500">
                      {Math.floor(costs.descript.includedMinutes / 60)} hrs included
                      {!costs.descript.sufficient && (
                        <span className="text-amber-600"> (max tier)</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      ${costs.descript.cost.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 text-right text-sm">
                    {savingsVsDescript > 0 ? (
                      <span className="text-green-600 dark:text-green-400">
                        +${savingsVsDescript.toFixed(2)} more
                      </span>
                    ) : savingsVsDescript < 0 ? (
                      <span className="text-gray-500">
                        ${Math.abs(savingsVsDescript).toFixed(2)} less
                      </span>
                    ) : (
                      <span className="text-gray-500">Same</span>
                    )}
                  </td>
                </tr>

                {/* Rev Row */}
                <tr>
                  <td className="py-4">
                    <span className="font-medium text-gray-900 dark:text-white">Rev</span>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-300">
                    {costs.rev.tier}
                    <div className="text-xs text-gray-500">
                      {Math.floor(costs.rev.includedMinutes / 60)} hrs included
                      {!costs.rev.sufficient && (
                        <span className="text-amber-600"> (max tier)</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      ${costs.rev.cost.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 text-right text-sm">
                    {savingsVsRev > 0 ? (
                      <span className="text-green-600 dark:text-green-400">
                        +${savingsVsRev.toFixed(2)} more
                      </span>
                    ) : savingsVsRev < 0 ? (
                      <span className="text-gray-500">
                        ${Math.abs(savingsVsRev).toFixed(2)} less
                      </span>
                    ) : (
                      <span className="text-gray-500">Same</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Math Transparency Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800/50">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            How We Calculate
          </h2>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
            <p>
              <strong>EchoNour:</strong> {ECHONOUR_FREE_MINUTES} minutes free, then $
              {ECHONOUR_PER_MINUTE.toFixed(2)}/minute ($6/hour). You only pay for what you use.
            </p>
            <p>
              <strong>Otter.ai:</strong> Subscription tiers — Free (5 hrs), Pro at $16.99/mo (20
              hrs), Business at $30/mo (100 hrs). We show the cheapest tier that covers your usage.
            </p>
            <p>
              <strong>Descript:</strong> Subscription tiers — Free (1 hr), Hobbyist at $12/mo (10
              hrs), Creator at $24/mo (30 hrs), Business at $44/mo (40 hrs).
            </p>
            <p>
              <strong>Rev:</strong> Subscription tiers — Free (45 min), Essentials at $29.99/mo (83
              hrs), Pro at $59.99/mo (166 hrs).
            </p>
          </div>
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Pricing last verified: February 2026. Competitor prices may change. This calculator uses
            most comparable plan tiers for content creators and podcasters.
          </p>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:bg-indigo-700 hover:shadow-xl"
          >
            Start Transcribing Free
            <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </Link>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            No credit card required • {ECHONOUR_FREE_MINUTES} minutes free
          </p>
        </div>

        {/* FAQ Section for SEO */}
        <div className="mt-16">
          <h2 className="mb-6 text-2xl font-bold text-gray-900 dark:text-white">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                How accurate is this calculator?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We verify competitor pricing regularly and show the most comparable plan tiers. For
                subscription services, we calculate the cheapest tier that covers your stated usage
                — no cherry-picking expensive tiers to make anyone look bad.
              </p>
            </div>
            <div>
              <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                Why is EchoNour sometimes more expensive for low usage?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                EchoNour uses pay-as-you-go pricing, which means you only pay for what you use. For
                very light usage (under 5 hours), competitors' free tiers might suffice. But as your
                needs grow, EchoNour's transparent per-minute pricing becomes significantly more
                economical.
              </p>
            </div>
            <div>
              <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                What about transcription quality?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Cost is only one factor. EchoNour uses state-of-the-art AI models and offers speaker
                diarization, timestamps, and AI-powered chat with your transcripts — all included in
                the per-minute price.
              </p>
            </div>
          </div>
        </div>
      </div>
  );
}
